import type { FunctionComponentWithRef } from "react";
import React, { useCallback, useState } from "react";
import { JsonLd } from "react-schemaorg";
import type { ImageObject } from "schema-dts";
import { removeAtMedia } from "scmp-sdk";

import { type ResponsiveVariants, theme } from "shared/lib/styles";

import type { ImageProps } from "./styles";
import { StyledImage } from "./styles";

export type Props = Omit<ImageProps, "$hasError"> &
  Omit<React.ImgHTMLAttributes<HTMLImageElement>, "height" | "loading" | "src" | "width"> & {
    className?: string;
    disableLinkingData?: boolean;
    lazyload?: boolean;
    responsiveVariant?: ResponsiveVariants<{
      aspectRatio: string;
      url: string;
      width: number;
    }>;
    src?: string;
  };

export const BaseImage: FunctionComponentWithRef<Props, HTMLImageElement> = ({
  disableLinkingData = false,
  lazyload = true,
  ref: forwardReference,
  responsiveVariant,
  src,
  title,
  ...attribs
}) => {
  const [hasError, setHasError] = useState<boolean | undefined>(undefined);

  const viewportSizeKeyMap = [
    { key: "desktopUp", value: theme.breakpoints.up("desktop") },
    { key: "tabletUp", value: theme.breakpoints.up("tablet") },
    { key: "mobileUp", value: theme.breakpoints.up("mobile") },
  ] as const;

  // Workaround event before hydration can't be listen. Reference https://github.com/facebook/react/issues/15446
  const captureElement = useCallback(
    (element: HTMLImageElement | null) => {
      if (element?.complete) {
        setHasError(element.naturalWidth === 0);
      }

      if (forwardReference) {
        if (typeof forwardReference === "function") {
          forwardReference(element);
        } else {
          (forwardReference as React.MutableRefObject<HTMLImageElement | null>).current = element;
        }
      }
    },
    [forwardReference],
  );

  const handleRenderImage = () => (
    <>
      <StyledImage
        {...attribs}
        $hasError={hasError}
        loading={lazyload ? "lazy" : "eager"}
        onError={() => {
          setHasError(true);
        }}
        onLoad={() => {
          setHasError(false);
        }}
        ref={captureElement}
        src={src}
        title={title}
      />
      {src && !disableLinkingData && (
        <JsonLd<ImageObject>
          item={{
            "@context": "https://schema.org",
            "@type": "ImageObject",
            caption: title,
            url: src,
          }}
        />
      )}
    </>
  );

  if (!responsiveVariant) return handleRenderImage();

  return (
    <picture>
      {viewportSizeKeyMap.map(({ key, value }, index) => (
        <source
          data-aspect-ratio={responsiveVariant?.[key].aspectRatio}
          data-width={responsiveVariant?.[key].width}
          key={index}
          media={removeAtMedia(value)}
          srcSet={responsiveVariant?.[key].url ?? undefined}
        />
      ))}
      {handleRenderImage()}
    </picture>
  );
};

BaseImage.displayName = "BaseImage";
